import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useMutation } from "@tanstack/react-query";
import { Text } from "@ui-kitten/components";
import Button from "components/Button";
import Chevron from "components/Chevron";
import ConfirmPurchaseButton from "components/ConfirmPurchaseButton";
import Container from "components/Container";
import DatePicker from "components/DatePicker/DatePicker";
import Footer from "components/Footer";
import Header from "components/Header";
import LoginOrSignupForm from "components/LoginOrSignupForm";
import PlanSelector from "components/PlanSelector";
import RenderHtml from "components/RenderHtml";
import Spacer from "components/Spacer";
import Stepper, { StepperContent } from "components/Stepper/Stepper";
import TenantLogo from "components/TenantLogo";
import TextInput from "components/TextInput";
import WhiteText from "components/WhiteText";
import dayjs from "dayjs";
import {
  PlanPackageDetails,
  PlanPackageValidationDetails,
} from "features/types";
import { Field, Formik } from "formik";
import { useAccount } from "hooks/useAccount";
import useAuth from "hooks/useAuth";
import useCurrentUser from "hooks/useCurrentUser";
import useSendConversionData from "hooks/useSendConversionData";
import { BuyGiftScreenProps } from "navigation/types";
import i18n from "plugins/localization";
import {
  fetchSignupChargeRequest,
  purchaseGiftsRequest,
} from "queries/account.queries";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import { useFetchPlanPackagesQuery } from "queries/planPackage.queries";
import { useFetchTenantBrandingQuery } from "queries/tenant.queries";
import React, { useEffect, useMemo, useState } from "react";
import { ImageBackground, Pressable, View } from "react-native";
import PaymentMethodCard from "screens/Account/components/PaymentMethodCard";
import QuantitySelector from "screens/Register/components/QuantitySelector";
import PriceText from "screens/Reserve/components/PriceText";
import { formatCurrency } from "utils/appUtils";
import { svgURL } from "utils/fileUtils";
import { toDashboard, toLogOut } from "utils/navigationUtils";
import { getEmailValidation, getPlanType } from "utils/planPackageUtils";
import * as yup from "yup";

interface StepTwoValues {
  firstName: string;
  lastName: string;
  email: string;
  deliverAt: Date;
}

const stepTwoInitialValues: StepTwoValues = {
  firstName: "",
  lastName: "",
  email: "",
  deliverAt: new Date(),
};

interface ValidationOptions {
  emailValidation?: PlanPackageValidationDetails;
}
const stepTwoValidationSchema = (options?: ValidationOptions) => {
  const { emailValidation } = options || {};
  return yup.object().shape({
    firstName: yup.string().required().label("First Name"),
    lastName: yup.string().required().label("Last Name"),
    email: (emailValidation
      ? yup
          .string()
          .matches(
            new RegExp(emailValidation.emailRegexPattern),
            emailValidation.errorMessage
          )
      : yup.string()
    )
      .email()
      .required()
      .label("Email"),
    deliverAt: yup.string().required(),
  });
};

const BuyGift = (props: BuyGiftScreenProps) => {
  // custom hooks
  const { isLoggedIn } = useAuth();
  const { currentUser } = useCurrentUser();
  const sendConversionData = useSendConversionData();

  // route params
  const planPackageId = props.route.params?.planPackageId;
  // queries
  const { mutateAsync: purchaseGift, isLoading } =
    useMutation(purchaseGiftsRequest);
  const { data: tenantConfig } = useFetchTenantConfigurationQuery();
  const { data: planPackages } = useFetchPlanPackagesQuery();
  const { data: branding } = useFetchTenantBrandingQuery();
  const { data: signupCharge, mutateAsync: fetchSignupCharge } = useMutation(
    fetchSignupChargeRequest
  );
  const { updateFetchAccountBffCache, account } = useAccount();

  // local states
  const [step, setStep] = useState(1);
  const [selectedPlanPackage, setSelectedPlanPackgae] =
    useState<PlanPackageDetails | null>(null);
  const [recipientInfo, setRecipientInfo] =
    useState<StepTwoValues>(stepTwoInitialValues);
  const [quantity, setQuantity] = useState(1);
  const [purchaseComplete, setPurchaseComplete] = useState(false);

  const shouldShowBackButton = useMemo(() => {
    if (step === 1) return false;
    return true;
  }, [step]);

  // Filter plan packages based on visibility
  const visiblePlanPackages = useMemo(() => {
    if (!planPackages) return [];
    return planPackages.filter((p) => {
      if (p.hiddenFromMembers) return false;
      // if plan package id is present in the url
      if (planPackageId) {
        return ["PUBLIC", "URL_ONLY", "URL_ACCOUNT"].includes(p.visibility);
      }
      return p.visibility === "PUBLIC";
    });
  }, [planPackages, planPackageId]);

  // Initializing selected plan package on page load
  useEffect(() => {
    if (visiblePlanPackages && visiblePlanPackages.length > 0) {
      // if plan package id param is present in the url
      // set selected plan package to the plan that has the same plan package id
      if (planPackageId !== undefined) {
        const planPackage = visiblePlanPackages.find(
          (p) => p.id === Number(planPackageId)
        );
        setSelectedPlanPackgae(planPackage || visiblePlanPackages[0]);
        return;
      }
      setSelectedPlanPackgae(visiblePlanPackages[0]);
    }
  }, [visiblePlanPackages]);

  // When selectedPlanPackage and quantity change, update signup charge
  useEffect(() => {
    if (!selectedPlanPackage || !tenantConfig) return;

    fetchSignupCharge({
      planPackageIds: new Array(quantity)
        .fill(1)
        .map(() => selectedPlanPackage.id),
      billingInterval: "FULL_SEASON",
      prorationDate: dayjs(recipientInfo.deliverAt)
        .tz(tenantConfig.timezone)
        .utc()
        .toISOString(),
    });
  }, [selectedPlanPackage, tenantConfig, quantity, recipientInfo.deliverAt]);

  // listen for auth state changes
  // when user logs in, set step to 3
  // when user logs out on the first step, do nothing, otherwise set step to 2
  useEffect(() => {
    if (isLoggedIn) {
      if (step === 2) {
        setStep(3);
      }
    } else {
      if (step === 1) return;
      setStep(2);
    }
  }, [isLoggedIn]);

  const toNextStep = () => setStep(step + 1);

  const onStepTwoFormSubmit = (values: StepTwoValues) => {
    setRecipientInfo(values);
    toNextStep();
  };

  const getTotalCharge = () => {
    if (!signupCharge) return 0;
    const totalFees = signupCharge.signupFees?.reduce(
      (total, fee) => total + Number(fee.feeAmount),
      0
    );
    return Number(signupCharge.signupCharge) + Number(totalFees);
  };
  const totalCharge = getTotalCharge();

  const onConfirmPurchase = async () => {
    const { email, firstName, lastName, deliverAt } = recipientInfo;
    const account = await purchaseGift({
      toUser: {
        email,
        firstName,
        lastName,
      },
      planPackageId: selectedPlanPackage!.id,
      quantity,
      deliverAt: dayjs(deliverAt)
        .set("hour", 9)
        .tz(tenantConfig?.timezone!)
        .utc(),
      source: "FANRALLY_APP",
    });
    updateFetchAccountBffCache(account);
    setPurchaseComplete(true);
    sendConversionData(signupCharge!, quantity);
  };

  const resetState = () => {
    setStep(1);
    setRecipientInfo(stepTwoInitialValues);
    setPurchaseComplete(false);
    setQuantity(1);
  };

  if (!selectedPlanPackage || !planPackages || !branding) {
    return <Text category="h6">Loading...</Text>;
  }

  if (selectedPlanPackage.disabled || !selectedPlanPackage.available) {
    return (
      <View>
        <Text category="h6" style={{ textAlign: "center", marginBottom: 15 }}>
          {i18n.t("screens.Register.comingSoon")}
        </Text>
      </View>
    );
  }

  if (purchaseComplete) {
    return (
      <View className="flex-1">
        <Header />
        <Container className="p-4 items-center justify-center flex-1 gap-4">
          <TenantLogo width={125} height={125} />
          <Text category="h6">
            Thank you for your purchase! You will receive a notification when
            your gift has been received.
          </Text>
          <Spacer />
          <Button style={{ width: 200 }} onPress={toDashboard}>
            Take Me Home
          </Button>
          <Spacer />
          <Button
            style={{ width: 200 }}
            onPress={() =>
              ((window as Window).location = tenantConfig!.buyflowRedirectUrl)
            }
          >
            Buy My Own Plan
          </Button>
          <Spacer />
          <Button style={{ width: 200 }} onPress={resetState}>
            Buy Another Gift
          </Button>
        </Container>
        <Footer />
      </View>
    );
  }

  return (
    <View className="flex-1">
      <Header programName={selectedPlanPackage.name} />
      <Container className="flex-grow items-center">
        <View className="max-w-3xl w-full py-4">
          <View>
            <View>
              {shouldShowBackButton && (
                <Pressable className="flex-row gap-2 my-4">
                  <Chevron direction="left" size={16} />
                  <Text category="s1" onPress={() => setStep(step - 1)}>
                    Back
                  </Text>
                </Pressable>
              )}
            </View>
            <ImageBackground
              source={{
                uri: svgURL(branding.name),
              }}
              imageStyle={{
                opacity: 0.2,
                zIndex: -1,
              }}
              className="px-8 py-12 rounded-xl"
              style={{
                backgroundColor: branding.primaryColor,
              }}
            >
              <WhiteText
                category="h6"
                style={{ marginTop: 4, textAlign: "center" }}
              >
                Purchase a Gift!
              </WhiteText>
            </ImageBackground>

            <View
              className="mt-4 md:border md:p-8 pt-4 md:rounded-xl md:bg-gray-50"
              style={{ borderColor: branding?.primaryColor }}
            >
              <Stepper
                step={step}
                steps={[
                  {
                    name: "Schedule",
                    icon: (
                      <MaterialCommunityIcons
                        name="account-edit"
                        size={16}
                        color="white"
                      />
                    ),
                  },
                  {
                    name: "Login",
                    icon: (
                      <MaterialCommunityIcons
                        name="login"
                        size={16}
                        color="white"
                      />
                    ),
                  },
                  {
                    name: "Payment",
                    icon: (
                      <MaterialCommunityIcons
                        name="credit-card"
                        size={16}
                        color="white"
                      />
                    ),
                  },
                ]}
              >
                <StepperContent>
                  <Spacer margin={4} />
                  {planPackageId === undefined && (
                    <>
                      <PlanSelector
                        label="Choose a Gift Plan"
                        planPackages={visiblePlanPackages}
                        onChange={setSelectedPlanPackgae}
                        selectedPlanPackage={selectedPlanPackage}
                      />
                      <Spacer />
                    </>
                  )}
                  {selectedPlanPackage && (
                    <>
                      <RenderHtml source={selectedPlanPackage.description} />
                      {getPlanType(selectedPlanPackage) !== "BANK" && (
                        <QuantitySelector
                          quantity={quantity}
                          setQuantity={setQuantity}
                        />
                      )}
                    </>
                  )}
                  <Spacer />
                  <Formik
                    validationSchema={stepTwoValidationSchema({
                      emailValidation: getEmailValidation(selectedPlanPackage),
                    })}
                    initialValues={stepTwoInitialValues}
                    onSubmit={onStepTwoFormSubmit}
                  >
                    {({ isValid, handleSubmit, values, setFieldValue }) => (
                      <View>
                        <View className="grid grid-cols-1 md:grid-cols-2 md:gap-4">
                          <Field
                            component={TextInput}
                            label="Recipient First Name"
                            name="firstName"
                            style={{ backgroundColor: "white" }}
                          />
                          <Field
                            component={TextInput}
                            label="Recipient Last Name"
                            name="lastName"
                            style={{ backgroundColor: "white" }}
                          />
                        </View>
                        <Field
                          component={TextInput}
                          name="email"
                          label="Recipient Email"
                          style={{ backgroundColor: "white" }}
                        />
                        <Field
                          component={DatePicker}
                          name="deliverAt"
                          label="Send My Gift On"
                          date={values.deliverAt}
                          onSelect={(newDate: Date) =>
                            setFieldValue("deliverAt", newDate)
                          }
                          style={{ backgroundColor: "white" }}
                        />
                        <Spacer />
                        <Button
                          disabled={!isValid}
                          onPress={() => handleSubmit()}
                        >
                          Continue
                        </Button>
                      </View>
                    )}
                  </Formik>
                </StepperContent>
                <StepperContent>
                  {isLoggedIn ? (
                    <Button onPress={() => setStep(step + 1)}>Continue</Button>
                  ) : (
                    <LoginOrSignupForm
                      initialForm="signup"
                      loginTitle="Must log in to send gifts"
                      signupTitle="To purchase your gift, first create an account"
                    />
                  )}
                </StepperContent>
                <StepperContent>
                  {account && account.billingLast4 && (
                    <PaymentMethodCard variant="outline" />
                  )}

                  {signupCharge !== undefined && (
                    <View className="my-4">
                      <View className="flex-row justify-between">
                        <Text>{selectedPlanPackage.name}</Text>
                        <Text>Qty: {quantity}</Text>
                      </View>
                      <Spacer />
                      <PriceText
                        label="Total"
                        text={formatCurrency(totalCharge)}
                        fontWeight="800"
                      />
                      <Spacer />
                      <ConfirmPurchaseButton
                        loading={isLoading}
                        charge={Number(totalCharge)}
                        onPress={onConfirmPurchase}
                      />
                    </View>
                  )}
                </StepperContent>
              </Stepper>

              {currentUser && (
                <View className="text-center mt-4">
                  <Text>
                    Logged in as:{" "}
                    <Text category="s1">{currentUser.firstName as string}</Text>
                    . Not you?{" "}
                    <Pressable onPress={toLogOut}>
                      <Text status="info">Log out</Text>
                    </Pressable>
                  </Text>
                </View>
              )}
            </View>
          </View>
        </View>
      </Container>
      <Footer />
    </View>
  );
};

export default BuyGift;
